/**
 * OverwhelmSkillEffect — Conjuration Swarm path evolution: replaces Swarm.
 *
 * Overwhelm replaces Swarm at XP threshold 1200. The room fills with 5 wisps
 * (vs. Swarm's 3) using coordinated suppression AI: each new wisp is assigned
 * to the enemy with the fewest existing wisp assignments, fanning out to cover
 * multiple angles without clustering.
 *
 * Mechanic:
 * - Spawn up to `maxWisps - currentCount` wisps (5 baseline, not a resolver key).
 * - Wisps use `aiMode: 'coordinated'` for load-balanced multi-target coverage.
 * - At cap (5): returns false, cooldown not consumed.
 * - Fizzle gate (freshness signal): if `targeting.shouldFizzle()` returns true,
 *   spawn count is reduced to `fizzleSpawnCount` (3, the Swarm-tier baseline)
 *   rather than blocking entirely — partial effectiveness is better than none.
 *
 * Coverage map (coordinated assignment):
 * - On each spawn, the skill queries `enemies.getAll()` and assigns the new
 *   wisp to the enemy with the fewest current wisp assignments
 *   (`_enemyCoverageMap`). This load-balances across distinct targets rather
 *   than piling all wisps onto the nearest enemy.
 * - The integration layer calls `onWispDied(wispId)` when SummonSystem fires
 *   'summon-died' for a wisp owned by Overwhelm. This decrements coverage so
 *   subsequent activations correctly reassign to newly uncovered enemies.
 * - If no enemies are visible, wisps spawn without a target assignment
 *   (integration layer handles wandering).
 *
 * Upgrade inheritance ('swarm' and 'summon' resolver scopes):
 * - maxWisps (swarm scope, max): Saturation contributes 4, but Overwhelm's
 *   baseline 5 dominates via Math.max(5, 4) = 5. Future upgrades above 5 win.
 * - spawnDistanceTiles, driftSpeedMultiplier (summon scope) from Outstretched
 * - hpMultiplier, aggroRedirectChance (summon scope) from Tenacious
 *
 * Medium cooldown tier (1200ms). Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Overwhelm
 */

import type { SkillEffect } from '../../SkillEffect.js';
import type { WispSummonConfig } from './SummonSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface OverwhelmDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMultiplicativeEffect(skillId: string, key: string, base?: number): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
  readonly player: {
    getPosition(): { x: number; y: number };
    getFacingDirection(): { x: number; y: number };
  };
  readonly collision: {
    isWallAt(x: number, y: number): boolean;
  };
  readonly targeting: {
    /** Returns true if the evolution freshness signal should reduce effectiveness. */
    shouldFizzle(): boolean;
  };
  readonly summons: {
    spawn(config: WispSummonConfig, x: number, y: number): string;
    /** Returns the number of currently living wisps. */
    getWispCount(): number;
  };
  readonly enemies: {
    /**
     * Returns all currently active enemies for wisp target assignment.
     * May be empty (no enemies in room) — wisps spawn unassigned in that case.
     */
    getAll(): ReadonlyArray<{ id: string; position: { x: number; y: number } }>;
  };
}

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface OverwhelmSkillConfig {
  readonly cooldownMs: number;
  /** Overwhelm-tier wisp cap baseline (5 per spec). Not a resolver key — hardcoded. */
  readonly maxWisps: number;
  /** Spawn count when the fizzle signal fires (Swarm-tier fallback). */
  readonly fizzleSpawnCount: number;
  /** Invoke/Overwhelm-tier wisp HP baseline. */
  readonly baseHp: number;
  /** Aggro weight for coordinated-mode wisps. */
  readonly baseAggroWeight: number;
  readonly baseSpawnDistanceTiles: number;
  readonly baseDriftSpeed: number;
  readonly leashRadiusTiles: number;
  readonly tileSize: number;
  readonly arcRangeDeg: number;
}

const DEFAULT_CONFIG: OverwhelmSkillConfig = {
  cooldownMs: 1200,              // medium cooldown tier (same as Swarm)
  maxWisps: 5,                   // Overwhelm baseline per design doc
  fizzleSpawnCount: 3,           // reduced to Swarm-tier on fizzle
  baseHp: 22,                    // Invoke/Overwhelm-tier wisp HP
  baseAggroWeight: 0.8,          // 80% aggro redirect
  baseSpawnDistanceTiles: 1,
  baseDriftSpeed: 1.0,
  leashRadiusTiles: 8,
  tileSize: 32,
  arcRangeDeg: 120,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class OverwhelmSkillEffect implements SkillEffect {
  readonly skillId = 'overwhelm';
  readonly type = 'active' as const;
  readonly activationMode = 'instant' as const;
  readonly failLabel = 'FIZZLE';

  private readonly deps: OverwhelmDeps;
  private readonly config: OverwhelmSkillConfig;

  private _cooldownRemaining = 0;

  // Observable state for scenarios and integration layer
  private _totalSpawns = 0;
  private _lastActivationSpawnCount = 0;
  private _fizzled = false;
  private _lastSpawnPositions: Array<{ x: number; y: number }> = [];
  private _lastSpawnIds: string[] = [];
  private _resolvedHp = 0;
  private _resolvedAggroWeight = 0;
  private _resolvedMaxWisps = 0;

  /**
   * Per-enemy wisp assignment counts. Incremented at spawn time (per wisp).
   * Decremented via onWispDied(). Exposed as a read-only view for tests and
   * the integration layer.
   */
  private readonly _enemyCoverageMap = new Map<string, number>();

  /**
   * Wisp ID → assigned enemy ID index. Allows onWispDied() to decrement the
   * correct enemy's coverage count without the caller needing to track targets.
   */
  private readonly _wispAssignments = new Map<string, string>();

  constructor(deps: OverwhelmDeps, config: Partial<OverwhelmSkillConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get cooldown(): number { return this._cooldownRemaining; }
  get totalSpawns(): number { return this._totalSpawns; }
  get lastActivationSpawnCount(): number { return this._lastActivationSpawnCount; }
  get fizzled(): boolean { return this._fizzled; }
  get lastSpawnPositions(): ReadonlyArray<{ x: number; y: number }> { return this._lastSpawnPositions; }
  get lastSpawnIds(): readonly string[] { return this._lastSpawnIds; }
  get resolvedHp(): number { return this._resolvedHp; }
  get resolvedAggroWeight(): number { return this._resolvedAggroWeight; }
  get resolvedMaxWisps(): number { return this._resolvedMaxWisps; }

  /**
   * Read-only view of enemy coverage map.
   * Key: enemy ID. Value: number of wisps currently assigned to that enemy.
   */
  get enemyCoverageMap(): ReadonlyMap<string, number> {
    return this._enemyCoverageMap;
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return this._cooldownRemaining <= 0;
  }

  activate(): boolean {
    if (!this.canActivate()) return false;

    // Resolve max wisp cap: Overwhelm baseline (5) dominates Saturation's max(4).
    // Future upgrades above 5 win via Math.max.
    const maxWispsOverride = this.deps.resolver.getMaxEffect('swarm', 'maxWisps');
    const maxWisps = maxWispsOverride !== undefined
      ? Math.max(this.config.maxWisps, maxWispsOverride)
      : this.config.maxWisps;
    this._resolvedMaxWisps = maxWisps;

    const currentCount = this.deps.summons.getWispCount();
    let toSpawn = Math.max(0, maxWisps - currentCount);

    if (toSpawn === 0) {
      // Already at cap — signal failure without consuming cooldown.
      this._lastActivationSpawnCount = 0;
      this._fizzled = false;
      return false;
    }

    // Freshness fizzle: reduce to Swarm-tier count rather than blocking entirely.
    this._fizzled = this.deps.targeting.shouldFizzle();
    if (this._fizzled) {
      toSpawn = Math.min(toSpawn, this.config.fizzleSpawnCount);
    }

    const pos = this.deps.player.getPosition();
    const facing = this.deps.player.getFacingDirection();

    // Resolve shared upgrade bonuses via 'summon' scope (Outstretched, Tenacious)
    const spawnDistanceBonus = this.deps.resolver.getAdditiveEffect('summon', 'spawnDistanceTiles');
    const distanceTiles = this.config.baseSpawnDistanceTiles + spawnDistanceBonus;

    const hpMultiplier = this.deps.resolver.getMultiplicativeEffect('summon', 'hpMultiplier', 1.0);
    const resolvedHp = Math.round(this.config.baseHp * hpMultiplier);
    this._resolvedHp = resolvedHp;

    const aggroOverride = this.deps.resolver.getMaxEffect('summon', 'aggroRedirectChance');
    const resolvedAggroWeight = aggroOverride !== undefined
      ? Math.max(this.config.baseAggroWeight, aggroOverride)
      : this.config.baseAggroWeight;
    this._resolvedAggroWeight = resolvedAggroWeight;

    const driftMultiplier = this.deps.resolver.getMultiplicativeEffect('summon', 'driftSpeedMultiplier', 1.0);
    const resolvedDriftSpeed = this.config.baseDriftSpeed * driftMultiplier;

    // Fetch current enemies for coordinated target assignment.
    const enemies = this.deps.enemies.getAll();

    // Spread wisps across 120° arc centered on facing direction.
    const arcRangeRad = (this.config.arcRangeDeg * Math.PI) / 180;
    const spawnIds: string[] = [];
    const spawnPositions: Array<{ x: number; y: number }> = [];

    for (let i = 0; i < toSpawn; i++) {
      const angleRad = toSpawn > 1
        ? (i / (toSpawn - 1) - 0.5) * arcRangeRad
        : 0;

      const cosA = Math.cos(angleRad);
      const sinA = Math.sin(angleRad);
      const dirX = facing.x * cosA - facing.y * sinA;
      const dirY = facing.x * sinA + facing.y * cosA;

      const spawnX = pos.x + dirX * distanceTiles * this.config.tileSize;
      const spawnY = pos.y + dirY * distanceTiles * this.config.tileSize;

      let finalX = spawnX;
      let finalY = spawnY;
      if (this.deps.collision.isWallAt(spawnX, spawnY)) {
        finalX = pos.x;
        finalY = pos.y;
      }

      // Assign this wisp to the least-covered enemy (coordinated suppression).
      // Coverage map is updated immediately so the next wisp in this loop sees
      // the updated counts — wisps fan out across enemies rather than piling up.
      const assignedEnemyId = enemies.length > 0
        ? this._findLeastCoveredEnemy(enemies)
        : undefined;

      const wispId = `overwhelm_wisp_${this._totalSpawns + i}`;

      if (assignedEnemyId !== undefined) {
        this._enemyCoverageMap.set(
          assignedEnemyId,
          (this._enemyCoverageMap.get(assignedEnemyId) ?? 0) + 1,
        );
        this._wispAssignments.set(wispId, assignedEnemyId);
      }

      const wispConfig: WispSummonConfig = {
        id: wispId,
        archetype: 'wisp',
        baseHp: resolvedHp,
        baseDamage: 0,
        baseSpeed: resolvedDriftSpeed,
        leashRadiusTiles: this.config.leashRadiusTiles,
        aggroWeight: resolvedAggroWeight,
        aiMode: 'coordinated',
        assignedTargetId: assignedEnemyId,
      };

      spawnIds.push(this.deps.summons.spawn(wispConfig, finalX, finalY));
      spawnPositions.push({ x: finalX, y: finalY });
    }

    this._lastActivationSpawnCount = toSpawn;
    this._lastSpawnPositions = spawnPositions;
    this._lastSpawnIds = spawnIds;
    this._totalSpawns += toSpawn;
    this._cooldownRemaining = this.config.cooldownMs;

    return true;
  }

  /**
   * Called by the integration layer when a wisp owned by this skill dies.
   * Decrements the coverage count for the wisp's assigned enemy, keeping the
   * map accurate for subsequent spawn-assignment decisions.
   *
   * Must be called by the integration layer's 'summon-died' handler, guarded
   * on the wisp's skill ID being 'overwhelm'.
   *
   * @param wispId - The ID passed as `WispSummonConfig.id` at spawn time.
   */
  onWispDied(wispId: string): void {
    const targetId = this._wispAssignments.get(wispId);
    if (targetId === undefined) return;

    const current = this._enemyCoverageMap.get(targetId) ?? 0;
    if (current <= 1) {
      this._enemyCoverageMap.delete(targetId);
    } else {
      this._enemyCoverageMap.set(targetId, current - 1);
    }
    this._wispAssignments.delete(wispId);
  }

  deactivate(): void {
    // Instant skill — no toggle state
  }

  isActive(): boolean {
    return false;
  }

  update(delta: number): void {
    if (this._cooldownRemaining > 0) {
      this._cooldownRemaining = Math.max(0, this._cooldownRemaining - delta);
    }
  }

  destroy(): void {
    this._cooldownRemaining = 0;
    this._lastSpawnPositions = [];
    this._lastSpawnIds = [];
    this._enemyCoverageMap.clear();
    this._wispAssignments.clear();
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Returns the ID of the enemy with the lowest current wisp coverage count.
   * Ties broken by iteration order. Coverage map is updated by the caller
   * immediately after each call, so within-activation load-balancing is correct.
   */
  private _findLeastCoveredEnemy(
    enemies: ReadonlyArray<{ id: string }>,
  ): string {
    let minCount = Infinity;
    let minId = enemies[0]!.id;
    for (const enemy of enemies) {
      const count = this._enemyCoverageMap.get(enemy.id) ?? 0;
      if (count < minCount) {
        minCount = count;
        minId = enemy.id;
      }
    }
    return minId;
  }
}
